import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { getUser, updateUserBanStatus } from "../../../../api/userApi";
import { authStore } from "../../../../stores/authStore";
import { permissionStore, PermissionAction, ResourceType } from "../../../../stores/permissionStore";
import { useSnapshot } from "valtio";
import { Button } from "../../../../components/ui/button";
import { AlertTriangle, ArrowLeft, Ban, ShieldCheck, Edit } from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../../../../components/ui/card";
import { Badge } from "../../../../components/ui/badge";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
  DialogTrigger,
} from "../../../../components/ui/dialog";

const getInitials = (name) => {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("").toUpperCase();
};

export default function ViewUser() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const snap = useSnapshot(authStore);
  const permSnap = useSnapshot(permissionStore);
  const currentUser = snap?.user;
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showBanDialog, setShowBanDialog] = useState(false);
  const [isBanning, setIsBanning] = useState(false);

  // Permission checks
  const canReadUser = permSnap.can(PermissionAction.READ, ResourceType.USER);
  const canEditProfile = permSnap.can(PermissionAction.UPDATE, ResourceType.USER);

  const softRefresh = () => {
    navigate(location.pathname, { replace: true });
  };

  const fetchUser = async () => {
    try {
      setLoading(true);
      const response = await getUser(id);

      if (response && response.success && response.data) {
        setUser({
          ...response.data,
          profileImage: response.data.profileImage
            ? import.meta.env.VITE_CDN_LINK + response.data.profileImage
            : undefined,
        });
      } else {
        const errorMessage =
          response?.message || "Failed to fetch user details";
        setError(errorMessage);
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Error fetching user details";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchUser();
    } else {
      console.error("No user ID provided");
      setError("No user ID provided");
      setLoading(false);
    }
  }, [id]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Check READ permission first
  if (!canReadUser) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to view user details.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading user details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-red-500 text-lg">{error}</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">User not found</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto">
      {/* Back Button */}
      <div className="mb-6">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Users
        </Button>
      </div>

      {/* Main Profile Card */}
      <Card className="mb-8">
        <CardContent className="p-8">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-6">
              {/* Profile Picture */}
              <div className="w-32 h-32 rounded-lg overflow-hidden bg-gray-200 flex-shrink-0">
                {user.profileImage ? (
                  <img
                    src={user.profileImage}
                    alt={`${user.name || user.firstName} ${user.lastName || ""}`}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-[#5D93A5] flex items-center justify-center text-white text-5xl font-bold">
                    {getInitials(user.name)}
                  </div>
                )}
              </div>

              {/* User Info */}
              <div className="flex-1">
                <div className="mb-2">
                  <h1 className="text-3xl font-bold text-gray-900">
                    {user.name ||
                      `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
                      "Unknown User"}
                  </h1>
                  <p className="text-lg text-gray-600 mt-1">
                    {user.rankDesignation || user.role || "No designation"}
                  </p>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center space-x-8">
                    <div>
                      <span className="text-gray-600">Email: </span>
                      <span className="text-[#2B6A7F]">{user.email}</span>
                    </div>
                  </div>

                  <div className="flex items-center space-x-8">
                    <div>
                      <span className="text-gray-600">Gender: </span>
                      <span className="font-semibold">
                        {user.gender || "N/A"}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">Age: </span>
                      <span className="font-semibold">{user.age || "N/A"}</span>
                    </div>
                  </div>

                  {/* Status Badges */}
                  <div className="flex items-center space-x-3 mt-4">
                    <Badge variant="outline" className="px-3 py-1">
                      {user.role}
                    </Badge>
                    <Badge
                      variant={user.emailVerified ? "default" : "secondary"}
                      className="px-3 py-1"
                    >
                      {user.emailVerified
                        ? "Email Verified"
                        : "Email Not Verified"}
                    </Badge>
                    <Badge
                      variant={
                        user.accountSetupCompleted ? "default" : "secondary"
                      }
                      className="px-3 py-1"
                    >
                      {user.accountSetupCompleted
                        ? "Setup Complete"
                        : "Setup Pending"}
                    </Badge>
                    {user.ban && (
                      <Badge variant="destructive" className="px-3 py-1">
                        <AlertTriangle className="w-3 h-3 mr-1" />
                        Banned
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Edit Profile Button */}
            {canEditProfile && (
              <Button
                onClick={() => navigate(`/admin/users/edit/${id}`)}
                className="bg-[#5D93A5] text-white px-6"
              >
                <Edit className="w-4 h-4 mr-2" />
                Edit Profile
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Additional Details Section */}
      <div className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 mb-6">
          Additional Details
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Aadhaar Number */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">
              Aadhaar Number
            </label>
            <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
              <span className="text-gray-900">
                {user.aadhaarNumber || "N/A"}
              </span>
            </div>
          </div>

          {/* Last/Current Ship's Name */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">
              Last/Current Ship's Name
            </label>
            <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
              <span className="text-gray-900">
                {user.currentShipName || "N/A"}
              </span>
            </div>
          </div>

          {/* IMO Number */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">
              IMO Number
            </label>
            <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
              <span className="text-gray-900">
                {user.currentShipImo || "N/A"}
              </span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* SID Document */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">
              SID (Seafarer's Identity Document)
            </label>
            <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 flex items-center justify-between">
              <span className="text-gray-900">
                {user.seafarerSid || "doc.pdf"}
              </span>
              {user.seafarerSidDocument && (
                <Button variant="outline" size="sm">
                  View
                </Button>
              )}
            </div>
          </div>

          {/* Indian CDC Number */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">
              INDoS Number
            </label>
            <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
              <span className="text-gray-900">{user.indianCdc || "N/A"}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Account Activity Section */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle>Account Activity</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">
                Account Created
              </p>
              <p className="text-gray-900">{formatDate(user.createdAt)}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">
                Last Login
              </p>
              <p className="text-gray-900">
                {user.lastLoginAt
                  ? formatDate(user.lastLoginAt)
                  : "Never logged in"}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">
                Last Updated
              </p>
              <p className="text-gray-900">{formatDate(user.updatedAt)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Admin Actions */}
      {currentUser.role === "SUPER_ADMIN" && (
        <Card>
          <CardHeader>
            <CardTitle className="text-red-600">Admin Actions</CardTitle>
            <CardDescription>
              These actions are irreversible. Please proceed with caution.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Dialog open={showBanDialog} onOpenChange={setShowBanDialog}>
              <DialogTrigger asChild>
                <Button
                  className={`${user.ban ? "bg-green-500 hover:bg-green-600" : "bg-red-500 hover:bg-red-600"} text-white w-full md:w-auto`}
                  disabled={isBanning}
                >
                  {isBanning ? (
                    "Processing..."
                  ) : (
                    <>
                      {user.ban ? (
                        <ShieldCheck className="h-4 w-4 mr-2" />
                      ) : (
                        <Ban className="h-4 w-4 mr-2" />
                      )}
                      {user.ban ? "Unban User" : "Ban User"}
                    </>
                  )}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>
                    {user.ban ? "Unban User" : "Ban User"}
                  </DialogTitle>
                  <DialogDescription>
                    {user.ban
                      ? "Are you sure you want to unban this user? They will be able to access the platform again."
                      : "Are you sure you want to ban this user? They will no longer be able to access the platform."}
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <DialogClose asChild>
                    <Button variant="outline" disabled={isBanning}>
                      Cancel
                    </Button>
                  </DialogClose>
                  <Button
                    className={`${user.ban ? "bg-green-500 hover:bg-green-600" : "bg-red-500 hover:bg-red-600"} text-white`}
                    disabled={isBanning}
                    onClick={async () => {
                      try {
                        setIsBanning(true);
                        const response = await updateUserBanStatus(user.id, {
                          ban: !user.ban,
                          reason: user.ban
                            ? "User unbanned by admin"
                            : "User banned by admin",
                        });

                        if (response.user) {
                          setUser((prev) => ({
                            ...prev,
                            ban: !prev.ban,
                            banReason: user.ban ? null : "User banned by admin",
                          }));
                          toast.success(
                            `User ${user.ban ? "unbanned" : "banned"} successfully`
                          );
                          softRefresh();
                        } else {
                          toast.error(
                            response.message ||
                              `Failed to ${user.ban ? "unban" : "ban"} user`
                          );
                        }
                      } catch (error) {
                        console.error("Error updating ban status:", error);
                        toast.error(error.message || "An error occurred");
                      } finally {
                        setIsBanning(false);
                        setShowBanDialog(false);
                      }
                    }}
                  >
                    {isBanning
                      ? "Processing..."
                      : user.ban
                        ? "Unban User"
                        : "Ban User"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
